import { useState, useCallback, useEffect } from 'react';
import { supabase } from './useSupabase';
import { generateProposalToken, isValidProposalToken } from '@/mocks/template-config';
import { showToast } from '@/components/Toast';
import type { ProposalItem } from '@/mocks/proposals';

export type ProposalEvent = {
  id: string;
  proposal_id: string;
  event_type: string;
  message: string;
  created_at: string;
};

export type ProposalStatus =
  | 'draft'
  | 'sent'
  | 'viewed'
  | 'approved'
  | 'rejected'
  | 'revision';

export type ValidityFilter = 'all' | 'valid' | 'expiring-soon' | 'expired' | 'no-expiration';

export interface Proposal {
  id: string;
  token: string;
  company_id: string | null;
  client_name: string;
  client_company: string;
  client_phone: string;
  client_email: string;
  client_address: string;
  client_document: string;
  title: string;
  subtitle: string;
  status: ProposalStatus;
  total_value: number;
  subtotal: number;
  discount: number;
  service_value: number;
  installation_value: number;
  valid_until: string;
  items: ProposalItem[];
  created_at: string;
  updated_at: string;
  category: string;
  template_preset_id: string | null;
  proposal_number: string;
  description: string;
  execution_deadline: string;
  payment_terms: string;
  notes: string;
  advantages_text: string;
  advantages_image: string;
  cover_image: string;
  expires_at: string | null;
  validity_days: number | null;
}

export type ValidityOption = 10 | 15 | 20 | 30 | 60 | null;

export const VALIDITY_OPTIONS: { value: ValidityOption; label: string }[] = [
  { value: 10, label: '10 dias' },
  { value: 15, label: '15 dias' },
  { value: 20, label: '20 dias' },
  { value: 30, label: '30 dias' },
  { value: 60, label: '60 dias' },
  { value: null, label: 'Sem expiração' },
];

export function calculateExpiresAt(createdAt: string, validityDays: number | null): string | null {
  if (!validityDays || validityDays <= 0) return null;
  const created = new Date(createdAt);
  const expires = new Date(created);
  expires.setDate(expires.getDate() + validityDays);
  return expires.toISOString();
}

export function formatValidityDate(isoDate: string | null): string {
  if (!isoDate) return 'Sem expiração';
  return new Date(isoDate).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function getValidityStatus(proposal: Proposal): {
  label: string;
  color: 'green' | 'yellow' | 'red';
  icon: string;
  daysLeft: number | null;
  isExpired: boolean;
  isExpiringSoon: boolean;
} {
  if (!proposal.expires_at) {
    return { label: 'Válida', color: 'green', icon: 'ri-check-line', daysLeft: null, isExpired: false, isExpiringSoon: false };
  }
  const now = new Date();
  const expires = new Date(proposal.expires_at);
  const diffMs = expires.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return { label: 'Expirada', color: 'red', icon: 'ri-close-circle-line', daysLeft: diffDays, isExpired: true, isExpiringSoon: false };
  }
  if (diffDays <= 5) {
    return { label: 'Expira em breve', color: 'yellow', icon: 'ri-time-line', daysLeft: diffDays, isExpired: false, isExpiringSoon: true };
  }
  return { label: 'Válida', color: 'green', icon: 'ri-check-line', daysLeft: diffDays, isExpired: false, isExpiringSoon: false };
}

const STORAGE_KEY = 'eletronica2000_proposals';
const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;

// UUID real da Eletrônica 2000 no banco
const COMPANY_ID = '3d7dc2cc-f843-4d6f-9a9b-af0fc9e1d7a4';

function loadLocalProposals(): Proposal[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Proposal[];
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch {
    // ignore
  }
  return [];
}

function saveLocalProposals(proposals: Proposal[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(proposals));
}

/**
 * Faz merge entre propostas do servidor e propostas locais.
 * Preserva propostas locais que não existem no servidor (ex: criadas offline).
 * Atualiza propostas que existem em ambos com a versão do servidor.
 */
function mergeProposals(server: Proposal[], local: Proposal[]): Proposal[] {
  const serverMap = new Map(server.map((p) => [p.id, p]));
  const localMap = new Map(local.map((p) => [p.id, p]));

  const merged: Proposal[] = [];

  // 1) Adiciona todas do servidor (mais atualizadas)
  for (const s of server) {
    merged.push(s);
  }

  // 2) Adiciona propostas locais que NÃO existem no servidor
  for (const l of local) {
    if (!serverMap.has(l.id)) {
      merged.push(l);
    }
  }

  // Ordena por created_at descendente
  return merged.sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );
}

export function useProposals() {
  const [proposals, setProposals] = useState<Proposal[]>(loadLocalProposals);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchProposals = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: supaError } = await supabase
        .from('proposals')
        .select('*')
        .order('created_at', { ascending: false });
      if (supaError) throw supaError;
      const mapped = (data || []).map((d) => ({
        ...d,
        items: Array.isArray(d.items) ? d.items : [],
      })) as Proposal[];

      const local = loadLocalProposals();
      const merged = mergeProposals(mapped, local);
      setProposals(merged);
      saveLocalProposals(merged);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar propostas');
      showToast('Erro ao carregar propostas do servidor. Usando dados locais.', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProposals();
  }, [fetchProposals]);

  const list = useCallback(() => {
    return proposals;
  }, [proposals]);

  const getByToken = useCallback(
    async (token: string): Promise<Proposal | undefined> => {
      // 1) Tenta buscar no servidor (edge function pública)
      let serverProposal: Proposal | undefined = undefined;
      try {
        const res = await fetch(`${SUPABASE_URL}/functions/v1/public-proposal`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
        if (res.ok) {
          const { proposal } = await res.json();
          if (proposal) {
            serverProposal = {
              ...proposal,
              items: Array.isArray(proposal.items) ? proposal.items : [],
            } as Proposal;
            // Salva no localStorage para acesso offline futuro
            try {
              const all = loadLocalProposals();
              const filtered = all.filter((p) => p.token !== token && p.proposal_number !== token);
              const updated = [serverProposal, ...filtered];
              saveLocalProposals(updated);
            } catch {
              // ignore localStorage errors
            }
          }
        }
      } catch (err) {
        console.warn('Erro ao buscar proposta no servidor:', err);
      }

      if (serverProposal) {
        return serverProposal;
      }

      // 2) Fallback: busca diretamente no localStorage por token OU proposal_number
      try {
        const allLocal = loadLocalProposals();
        const local = allLocal.find((p) => p.token === token || p.proposal_number === token);
        if (local) return local;
      } catch {
        // ignore
      }

      // 3) Último fallback: estado atual do hook
      return proposals.find((p) => p.token === token || p.proposal_number === token);
    },
    [proposals]
  );

  const getById = useCallback(
    async (id: string): Promise<Proposal | undefined> => {
      try {
        const { data, error: supaError } = await supabase
          .from('proposals')
          .select('*')
          .eq('id', id)
          .maybeSingle();
        if (supaError) throw supaError;
        if (data) {
          return { ...data, items: Array.isArray(data.items) ? data.items : [] } as Proposal;
        }
      } catch (err) {
        showToast('Erro ao buscar proposta por ID. Verifique sua conexão.', 'error');
        // fallback local
      }
      return proposals.find((p) => p.id === id);
    },
    [proposals]
  );

  const create = useCallback(
    async (data: Omit<Proposal, 'id' | 'token' | 'created_at' | 'updated_at' | 'expires_at'>): Promise<Proposal> => {
      const now = new Date().toISOString();
      // Usar proposal_number como token se for válido (código que o cliente digita = acesso)
      const token = (data.proposal_number && isValidProposalToken(data.proposal_number))
        ? data.proposal_number
        : generateProposalToken();
      const expiresAt = data.validity_days ? calculateExpiresAt(now, data.validity_days) : null;
      const validUntil = expiresAt ? formatValidityDate(expiresAt) : 'Sem expiração';
      // Remove campos que não existem no schema do banco
      const { title, subtitle, ...rest } = data as Record<string, unknown>;
      const payload = {
        ...rest,
        company_id: COMPANY_ID,
        token,
        created_at: now,
        updated_at: now,
        expires_at: expiresAt,
        valid_until: validUntil,
      };
      try {
        const { data: inserted, error: supaError } = await supabase
          .from('proposals')
          .insert(payload)
          .select()
          .single();
        if (supaError) throw supaError;
        const mapped = { ...inserted, items: Array.isArray(inserted.items) ? inserted.items : [] } as Proposal;
        const updated = [mapped, ...proposals];
        setProposals(updated);
        saveLocalProposals(updated);
        return mapped;
      } catch (err) {
        showToast('Erro ao salvar proposta no servidor. Salvado localmente.', 'warning');
        const fallback: Proposal = {
          ...data,
          id: `prop-${Date.now()}`,
          token,
          created_at: now,
          updated_at: now,
          expires_at: expiresAt,
          valid_until: validUntil,
        };
        const updated = [fallback, ...proposals];
        setProposals(updated);
        saveLocalProposals(updated);
        return fallback;
      }
    },
    [proposals]
  );

  const update = useCallback(
    async (id: string, data: Partial<Proposal>): Promise<Proposal | undefined> => {
      const now = new Date().toISOString();
      // Remove campos que não existem no schema do banco
      const { title, subtitle, ...rest } = data as Record<string, unknown>;
      const updatePayload: Record<string, unknown> = { ...rest, updated_at: now };
      if (data.items) {
        const sub = data.items.reduce((sum, item) => sum + item.total, 0);
        updatePayload.subtotal = sub;
        updatePayload.total_value = sub + (data.service_value ?? 0) + (data.installation_value ?? 0) - (data.discount ?? proposals.find((p) => p.id === id)?.discount ?? 0);
      }
      if ((data.discount !== undefined || data.service_value !== undefined || data.installation_value !== undefined) && data.items === undefined) {
        const proposal = proposals.find((p) => p.id === id);
        if (proposal) {
          updatePayload.total_value = proposal.subtotal + (data.service_value ?? proposal.service_value ?? 0) + (data.installation_value ?? proposal.installation_value ?? 0) - (data.discount ?? proposal.discount ?? 0);
        }
      }
      if (data.validity_days !== undefined) {
        const proposal = proposals.find((p) => p.id === id);
        const createdAt = data.created_at ?? proposal?.created_at ?? now;
        updatePayload.expires_at = data.validity_days ? calculateExpiresAt(createdAt, data.validity_days) : null;
        updatePayload.valid_until = updatePayload.expires_at ? formatValidityDate(updatePayload.expires_at as string) : 'Sem expiração';
      }
      try {
        const { data: updatedData, error: supaError } = await supabase
          .from('proposals')
          .update(updatePayload)
          .eq('id', id)
          .select()
          .single();
        if (supaError) throw supaError;
        const mapped = { ...updatedData, items: Array.isArray(updatedData.items) ? updatedData.items : [] } as Proposal;
        const updated = proposals.map((p) => (p.id === id ? mapped : p));
        setProposals(updated);
        saveLocalProposals(updated);
        return mapped;
      } catch (err) {
        showToast('Erro ao atualizar proposta no servidor. Atualizado localmente.', 'warning');
        const updated = proposals.map((p) => {
          if (p.id !== id) return p;
          const next = { ...p, ...data, updated_at: now } as Proposal;
          if (data.items) {
            next.subtotal = data.items.reduce((sum, item) => sum + item.total, 0);
            next.total_value = next.subtotal + (next.service_value || 0) + (next.installation_value || 0) - (next.discount || 0);
          }
          if (data.discount !== undefined && data.items === undefined) {
            next.total_value = next.subtotal + (next.service_value || 0) + (next.installation_value || 0) - data.discount;
          }
          if (data.service_value !== undefined && data.items === undefined) {
            next.total_value = next.subtotal + data.service_value + (next.installation_value || 0) - (next.discount || 0);
          }
          if (data.installation_value !== undefined && data.items === undefined) {
            next.total_value = next.subtotal + (next.service_value || 0) + data.installation_value - (next.discount || 0);
          }
          if (data.validity_days !== undefined) {
            next.expires_at = data.validity_days ? calculateExpiresAt(next.created_at, data.validity_days) : null;
            next.valid_until = next.expires_at ? formatValidityDate(next.expires_at) : 'Sem expiração';
          }
          return next;
        });
        setProposals(updated);
        saveLocalProposals(updated);
        return updated.find((p) => p.id === id);
      }
    },
    [proposals]
  );

  const duplicate = useCallback(
    async (id: string): Promise<Proposal | undefined> => {
      const original = proposals.find((p) => p.id === id);
      if (!original) return undefined;
      const now = new Date().toISOString();
      const expiresAt = original.validity_days ? calculateExpiresAt(now, original.validity_days) : null;
      const validUntil = expiresAt ? formatValidityDate(expiresAt) : 'Sem expiração';
      // Remove campos que não existem no schema do banco
      const { title, subtitle, id: _id, ...restOriginal } = original as unknown as Record<string, unknown>;
      const payload = {
        ...restOriginal,
        company_id: COMPANY_ID,
        token: generateProposalToken(),
        proposal_number: `${original.proposal_number} (Cópia)`,
        status: 'draft' as ProposalStatus,
        created_at: now,
        updated_at: now,
        expires_at: expiresAt,
        valid_until: validUntil,
      };
      try {
        const { data: inserted, error: supaError } = await supabase
          .from('proposals')
          .insert(payload)
          .select()
          .single();
        if (supaError) throw supaError;
        const mapped = { ...inserted, items: Array.isArray(inserted.items) ? inserted.items : [] } as Proposal;
        const updated = [mapped, ...proposals];
        setProposals(updated);
        saveLocalProposals(updated);
        return mapped;
      } catch (err) {
        showToast('Erro ao duplicar proposta no servidor. Duplicada localmente.', 'warning');
        const fallback: Proposal = { ...original, id: `prop-${Date.now()}`, token: generateProposalToken(), proposal_number: `${original.proposal_number} (Cópia)`, status: 'draft', created_at: now, updated_at: now, expires_at: expiresAt, valid_until: validUntil };
        const updated = [fallback, ...proposals];
        setProposals(updated);
        saveLocalProposals(updated);
        return fallback;
      }
    },
    [proposals]
  );

  const remove = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const { error: supaError } = await supabase.from('proposals').delete().eq('id', id);
        if (supaError) throw supaError;
      } catch (err) {
        showToast('Erro ao remover proposta no servidor. Removida localmente.', 'warning');
      }
      const updated = proposals.filter((p) => p.id !== id);
      if (updated.length === proposals.length) return false;
      setProposals(updated);
      saveLocalProposals(updated);
      return true;
    },
    [proposals]
  );

  const renew = useCallback(
    async (id: string, validityDays: number | null): Promise<Proposal | undefined> => {
      const now = new Date().toISOString();
      const expiresAt = validityDays ? calculateExpiresAt(now, validityDays) : null;
      const validUntil = expiresAt ? formatValidityDate(expiresAt) : 'Sem expiração';
      try {
        const res = await fetch(`${SUPABASE_URL}/functions/v1/proposals/${id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token ?? ''}`,
          },
          body: JSON.stringify({ _renew: true, validity_days: validityDays }),
        });
        if (!res.ok) throw new Error('Erro ao renovar proposta');
        const { data: updatedData } = await res.json();
        const mapped = {
          ...updatedData,
          items: Array.isArray(updatedData.items) ? updatedData.items : [],
        } as Proposal;
        const updated = proposals.map((p) => (p.id === id ? mapped : p));
        setProposals(updated);
        saveLocalProposals(updated);
        return mapped;
      } catch (err) {
        showToast('Erro ao renovar proposta no servidor. Renovada localmente.', 'warning');
        // fallback local
        const updated = proposals.map((p) => {
          if (p.id !== id) return p;
          return {
            ...p,
            validity_days: validityDays,
            expires_at: expiresAt,
            valid_until: validUntil,
            updated_at: now,
          };
        });
        setProposals(updated);
        saveLocalProposals(updated);
        return updated.find((p) => p.id === id);
      }
    },
    [proposals]
  );

  const getEvents = useCallback(
    async (proposalId: string): Promise<ProposalEvent[]> => {
      try {
        const { data, error } = await supabase
          .from('proposal_events')
          .select('*')
          .eq('proposal_id', proposalId)
          .order('created_at', { ascending: false });
        if (error) throw error;
        return (data || []) as ProposalEvent[];
      } catch (err) {
        showToast('Erro ao carregar histórico de eventos.', 'error');
        return [];
      }
    },
    []
  );

  const approve = useCallback(
    async (id: string): Promise<Proposal | undefined> => {
      const now = new Date().toISOString();
      try {
        const { data: updatedData, error: supaError } = await supabase
          .from('proposals')
          .update({ status: 'approved', updated_at: now })
          .eq('id', id)
          .select()
          .single();
        if (supaError) throw supaError;
        const mapped = { ...updatedData, items: Array.isArray(updatedData.items) ? updatedData.items : [] } as Proposal;
        const updated = proposals.map((p) => (p.id === id ? mapped : p));
        setProposals(updated);
        saveLocalProposals(updated);
        // registrar evento
        await supabase.from('proposal_events').insert({
          proposal_id: id,
          event_type: 'proposal_approved',
          message: 'Proposta aprovada pelo cliente via portal.',
        });
        return mapped;
      } catch (err) {
        showToast('Erro ao aprovar proposta no servidor. Aprovada localmente.', 'warning');
        const updated = proposals.map((p) =>
          p.id === id ? { ...p, status: 'approved' as ProposalStatus, updated_at: now } : p
        );
        setProposals(updated);
        saveLocalProposals(updated);
        return updated.find((p) => p.id === id);
      }
    },
    [proposals]
  );

  const markViewed = useCallback(
    async (id: string): Promise<Proposal | undefined> => {
      const now = new Date().toISOString();
      // Status que podem virar 'viewed': draft e sent
      const canBecomeViewed = ['draft', 'sent'];
      try {
        const { data: updatedData, error: supaError } = await supabase
          .from('proposals')
          .update({ status: 'viewed', updated_at: now })
          .eq('id', id)
          .in('status', canBecomeViewed)
          .select()
          .single();
        if (supaError) {
          // Se não achou nada (status já não é 'draft' nem 'sent'), ignora silenciosamente
          if (supaError.code === 'PGRST116') {
            return proposals.find((p) => p.id === id);
          }
          throw supaError;
        }
        const mapped = { ...updatedData, items: Array.isArray(updatedData.items) ? updatedData.items : [] } as Proposal;
        const updated = proposals.map((p) => (p.id === id ? mapped : p));
        setProposals(updated);
        saveLocalProposals(updated);
        return mapped;
      } catch (err) {
        // fallback local: só atualiza se o status atual permitir
        const current = proposals.find((p) => p.id === id);
        if (current && canBecomeViewed.includes(current.status)) {
          const updated = proposals.map((p) =>
            p.id === id ? { ...p, status: 'viewed' as ProposalStatus, updated_at: now } : p
          );
          setProposals(updated);
          saveLocalProposals(updated);
          return updated.find((p) => p.id === id);
        }
        return current;
      }
    },
    [proposals]
  );

  return { proposals, loading, error, list, getByToken, getById, create, update, duplicate, remove, renew, getEvents, approve, markViewed, refresh: fetchProposals };
}